import { useCallback, useEffect, useRef, useState } from "react";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import {
  GOOGLE_MAPS_API_KEY,
  loadGoogleMapsPlaces,
  placeLabelFromSelection,
} from "@/lib/googlePlaces";

const DROPDOWN_ESTIMATED_HEIGHT = 280;

export default function CityGoogleAutocomplete({
  value,
  onChange,
  onSelect,
  platformCities = [],
  placeholder = "Search city…",
  className,
  containerTestId = "req-city-select",
  inputTestId = "req-city-search",
  predictionsPlacement = "bottom",
}) {
  const rootRef = useRef(null);
  const containerRef = useRef(null);
  const autocompleteRef = useRef(null);
  const onChangeRef = useRef(onChange);
  const onSelectRef = useRef(onSelect);
  const [isReady, setIsReady] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [dropUp, setDropUp] = useState(predictionsPlacement === "top");

  onChangeRef.current = onChange;
  onSelectRef.current = onSelect;

  const updatePredictionsPlacement = useCallback(() => {
    const widget = autocompleteRef.current;
    const host = containerRef.current;
    if (!host) return;

    const rect = host.getBoundingClientRect();
    let shouldDropUp = predictionsPlacement === "top";

    if (predictionsPlacement === "auto") {
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;
      shouldDropUp =
        spaceBelow < DROPDOWN_ESTIMATED_HEIGHT && spaceAbove > spaceBelow;
    }

    setDropUp(shouldDropUp);

    if (!widget) return;

    if (shouldDropUp) {
      widget.style.setProperty("--gpa-anchor-top", `${rect.top}px`);
      widget.style.setProperty("--gpa-anchor-left", `${rect.left}px`);
      widget.style.setProperty("--gpa-anchor-width", `${rect.width}px`);
    } else {
      widget.style.removeProperty("--gpa-anchor-top");
      widget.style.removeProperty("--gpa-anchor-left");
      widget.style.removeProperty("--gpa-anchor-width");
    }
  }, [predictionsPlacement]);

  useEffect(() => {
    if (!GOOGLE_MAPS_API_KEY) return undefined;

    let cancelled = false;

    loadGoogleMapsPlaces()
      .then(() => {
        if (!cancelled) {
          setLoadError("");
          setIsReady(true);
        }
      })
      .catch((error) => {
        console.error(error);
        if (!cancelled) {
          setLoadError(
            error?.message || "Google Places failed to load. Check your API key and enabled APIs."
          );
        }
      });

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!isReady || !containerRef.current) return undefined;

    let autocomplete = null;
    let cancelled = false;

    const initAutocomplete = async () => {
      const { PlaceAutocompleteElement } = await window.google.maps.importLibrary("places");
      if (cancelled || !containerRef.current) return;

      autocomplete = new PlaceAutocompleteElement({
        placeholder,
        value: value || "",
      });
      autocomplete.className = "city-google-autocomplete-widget";

      const handleSelect = (event) => {
        const placePrediction = event.placePrediction;
        const selectedText = placeLabelFromSelection(null, placePrediction);
        if (!selectedText) return;

        autocomplete.value = selectedText;
        onChangeRef.current(selectedText);
        onSelectRef.current?.(selectedText);
      };

      const handleInput = () => {
        onChangeRef.current(String(autocomplete.value || ""));
        updatePredictionsPlacement();
      };

      const handleFocus = () => {
        updatePredictionsPlacement();
      };

      const handleError = (event) => {
        console.error("Google Places autocomplete error", event);
        setLoadError(
          "Google Places request failed. Enable Places API (New) and Maps JavaScript API for this key."
        );
      };

      autocomplete.addEventListener("gmp-select", handleSelect);
      autocomplete.addEventListener("input", handleInput);
      autocomplete.addEventListener("focus", handleFocus, true);
      autocomplete.addEventListener("gmp-error", handleError);

      containerRef.current.replaceChildren(autocomplete);
      autocompleteRef.current = autocomplete;
      updatePredictionsPlacement();
    };

    initAutocomplete().catch((error) => {
      console.error(error);
      setLoadError(error?.message || "Google Places failed to initialize.");
    });

    return () => {
      cancelled = true;
      if (autocomplete) {
        autocomplete.remove();
      }
      autocompleteRef.current = null;
      if (containerRef.current) {
        containerRef.current.replaceChildren();
      }
    };
  }, [isReady, placeholder, updatePredictionsPlacement]);

  useEffect(() => {
    if (!isReady || predictionsPlacement === "bottom") return undefined;

    const onReposition = () => updatePredictionsPlacement();
    window.addEventListener("resize", onReposition);
    window.addEventListener("scroll", onReposition, true);
    return () => {
      window.removeEventListener("resize", onReposition);
      window.removeEventListener("scroll", onReposition, true);
    };
  }, [isReady, predictionsPlacement, updatePredictionsPlacement]);

  useEffect(() => {
    const autocomplete = autocompleteRef.current;
    if (!autocomplete) return;
    const nextValue = value || "";
    if (autocomplete.value !== nextValue) {
      autocomplete.value = nextValue;
    }
  }, [value]);

  if (!GOOGLE_MAPS_API_KEY) {
    return (
      <div className={cn("city-google-autocomplete", className)} data-testid={containerTestId}>
        <Input
          data-testid={inputTestId}
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          className="rounded-none border-neutral-300"
        />
        {platformCities.length > 0 ? (
          <div className="mt-2 flex flex-wrap gap-1.5">
            {platformCities.map((cityName) => (
              <button
                key={cityName}
                type="button"
                className={cn(
                  "rounded-full border px-2.5 py-1 text-xs font-medium transition-colors",
                  value === cityName
                    ? "border-orange-300 bg-orange-50 text-orange-700"
                    : "border-neutral-200 bg-white text-neutral-600 hover:border-neutral-300 hover:bg-neutral-50"
                )}
                onClick={() => onChange(cityName)}
              >
                {cityName}
              </button>
            ))}
          </div>
        ) : null}
      </div>
    );
  }

  return (
    <div
      ref={rootRef}
      className={cn(
        "city-google-autocomplete",
        dropUp && "city-google-autocomplete--drop-up",
        className
      )}
      data-testid={containerTestId}
    >
      <div ref={containerRef} className="city-google-autocomplete-host" data-testid={inputTestId} />
      {loadError ? (
        <p className="mt-1 text-xs text-red-600">{loadError}</p>
      ) : null}
      {platformCities.length > 0 ? (
        <div className="mt-2 flex flex-wrap gap-1.5">
          {platformCities.map((cityName) => (
            <button
              key={cityName}
              type="button"
              className={cn(
                "rounded-full border px-2.5 py-1 text-xs font-medium transition-colors",
                value === cityName
                  ? "border-orange-300 bg-orange-50 text-orange-700"
                  : "border-neutral-200 bg-white text-neutral-600 hover:border-neutral-300 hover:bg-neutral-50"
              )}
              onClick={() => onChange(cityName)}
            >
              {cityName}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}
